import { useEffect, useMemo, useRef, useState } from "react";

import {
  CheckCircle,
  Delete,
  Error as ErrorIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Person,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import Papa from "papaparse";
import { useTranslation } from "react-i18next";

import { apiReportClient } from "@/api";
import { getServiceConfig } from "@/common/serviceConfigurations";
import {
  SUB_PERMISSION_KEYS,
  assignableAppPermissions,
  getAppDef,
  getParentDef,
} from "@/components/UserManagement/permissionTree";
import { Permission } from "@/components/UserManagement/types";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useRequireInternalUserId } from "@/hooks/useRequireInternalUserId";

import { FileSelectPhase } from "./FileSelectPhase";
import { grantPermissions } from "./grantPermissions";
import { PreviewPhase } from "./PreviewPhase";
import { ResultsPhase } from "./ResultsPhase";
import { CsvRawRow, DisplayRow, UploadResult, UploadStatus } from "./types";
import { UploadSpinner } from "./UploadSpinner";
import {
  extractErrorMsg,
  processRows,
  recheckDuplicates,
  validateRow,
} from "./utils";

interface UploadUsersCSVDialogProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onUploadSuccess?: () => void;
  existingUsers?: { email: string; id: string }[];
}

const UploadUsersCSVDialog = ({
  open,
  onClose,
  orgId,
  onUploadSuccess,
  existingUsers,
}: UploadUsersCSVDialogProps) => {
  const { isDevEnv } = useDevEnvironment();
  const { t } = useTranslation("common");
  const { isRequired: isInternalUserIdSetting } =
    useRequireInternalUserId(orgId);
  const orgSettingsLoaded = isInternalUserIdSetting !== undefined;
  const requireInternalUserId = isInternalUserIdSetting ?? false;

  const existingEmails = useMemo(
    () =>
      new Set((existingUsers ?? []).map((u) => u.email.toLowerCase().trim())),
    [existingUsers],
  );

  const [displayRows, setDisplayRows] = useState<DisplayRow[]>([]);
  const [duplicatesRemoved, setDuplicatesRemoved] = useState(0);
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [uploadProgress, setUploadProgress] = useState({
    completed: 0,
    total: 0,
  });
  const [userCreationResults, setUserCreationResults] = useState<
    UploadResult[]
  >([]);
  const [permissionResults, setPermissionResults] = useState<UploadResult[]>(
    [],
  );

  const [expandedApps, setExpandedApps] = useState<Set<Permission>>(new Set());

  const toggleAppExpanded = (appKey: Permission) => {
    setExpandedApps((prev) => {
      const next = new Set(prev);
      if (next.has(appKey)) next.delete(appKey);
      else next.add(appKey);
      return next;
    });
  };

  const resetState = () => {
    setDisplayRows([]);
    setDuplicatesRemoved(0);
    setFileName("");
    setUploadStatus("idle");
    setUploadProgress({ completed: 0, total: 0 });
    setUserCreationResults([]);
    setPermissionResults([]);
    setExpandedApps(new Set());
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse<CsvRawRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const { rows, duplicatesRemoved: dupes } = processRows(
          results.data,
          requireInternalUserId,
        );
        setDisplayRows(
          recheckDuplicates(rows, existingEmails, requireInternalUserId),
        );
        setDuplicatesRemoved(dupes);
      },
    });
  };

  useEffect(() => {
    setDisplayRows((prev) =>
      recheckDuplicates(prev, existingEmails, requireInternalUserId),
    );
  }, [requireInternalUserId, existingEmails]);

  const processRowUpdate = (newRow: DisplayRow): DisplayRow => {
    const email = newRow.email.toLowerCase().trim();
    const processed: DisplayRow = {
      ...newRow,
      email,
      errors: validateRow(
        { ...newRow, isExisting: existingEmails.has(email) },
        requireInternalUserId,
      ),
      isExisting: existingEmails.has(email),
    };

    setDisplayRows((prev) => {
      const updated = prev.map((r) => (r.id === processed.id ? processed : r));
      return recheckDuplicates(updated, existingEmails, requireInternalUserId);
    });

    return processed;
  };

  const togglePermission = (rowId: number, permission: Permission) => {
    setDisplayRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;

        const has = row.permissions.includes(permission);

        if (SUB_PERMISSION_KEYS.has(permission)) {
          if (has) {
            return {
              ...row,
              permissions: row.permissions.filter((p) => p !== permission),
            };
          } else {
            const parentDef = getParentDef(permission);
            const toAdd: Permission[] = [permission];
            if (parentDef && !row.permissions.includes(parentDef.permission)) {
              toAdd.push(parentDef.permission);
            }
            return {
              ...row,
              permissions: [...new Set([...row.permissions, ...toAdd])],
            };
          }
        } else {
          const appDef = getAppDef(permission);
          const subKeys =
            appDef?.subPermissions?.map((s) => s.permission) ?? [];

          if (has) {
            const toRemove = new Set([permission, ...subKeys]);
            return {
              ...row,
              permissions: row.permissions.filter((p) => !toRemove.has(p)),
            };
          } else {
            return {
              ...row,
              permissions: [
                ...new Set([...row.permissions, permission, ...subKeys]),
              ],
            };
          }
        }
      }),
    );
  };

  const toggleAllPermission = (permission: Permission) => {
    setDisplayRows((prev) => {
      const validIds = new Set(
        prev.filter((r) => r.errors.length === 0).map((r) => r.id),
      );
      const allChecked = prev
        .filter((r) => validIds.has(r.id))
        .every((r) => r.permissions.includes(permission));

      return prev.map((row) => {
        if (!validIds.has(row.id)) return row;

        if (SUB_PERMISSION_KEYS.has(permission)) {
          if (allChecked) {
            return {
              ...row,
              permissions: row.permissions.filter((p) => p !== permission),
            };
          } else {
            const parentDef = getParentDef(permission);
            const toAdd: Permission[] = [permission];
            if (parentDef && !row.permissions.includes(parentDef.permission)) {
              toAdd.push(parentDef.permission);
            }
            return {
              ...row,
              permissions: [...new Set([...row.permissions, ...toAdd])],
            };
          }
        } else {
          const appDef = getAppDef(permission);
          const subKeys =
            appDef?.subPermissions?.map((s) => s.permission) ?? [];

          if (allChecked) {
            const toRemove = new Set([permission, ...subKeys]);
            return {
              ...row,
              permissions: row.permissions.filter((p) => !toRemove.has(p)),
            };
          } else {
            return {
              ...row,
              permissions: [
                ...new Set([...row.permissions, permission, ...subKeys]),
              ],
            };
          }
        }
      });
    });
  };

  // Step 1: create new users only (existing users are skipped)
  const handleUploadUsers = async () => {
    const newRowsToCreate = displayRows.filter(
      (r) => r.errors.length === 0 && !r.isExisting,
    );

    if (newRowsToCreate.length === 0) {
      setUserCreationResults([]);
      setUploadStatus("users_done");
      return;
    }

    setUploadStatus("uploading_users");
    setUploadProgress({ completed: 0, total: newRowsToCreate.length });

    const results: UploadResult[] = [];

    for (const row of newRowsToCreate) {
      try {
        const response = await apiReportClient.putUser(orgId, {
          first_name: row.first_name,
          last_name: row.last_name,
          email: row.email,
          badge_number: row.badge_number,
          rank_short: row.rank_short,
          contact_phone_number: row.contact_phone_number,
          internal_user_id: row.internal_user_id || undefined,
          send_invite: false,
        });
        results.push({ row, userId: response.id });
      } catch (error) {
        results.push({ row, error: extractErrorMsg(error) });
      }
      setUploadProgress((prev) => ({ ...prev, completed: prev.completed + 1 }));
    }

    setUserCreationResults(results);
    setUploadStatus("users_done");
  };

  // Step 2: grant permissions to all valid rows (new + existing)
  const handleGrantPermissions = async () => {
    const emailToUserId = new Map<string, string>();
    for (const user of existingUsers ?? []) {
      emailToUserId.set(user.email.toLowerCase().trim(), user.id);
    }
    for (const result of userCreationResults) {
      if (result.userId) {
        emailToUserId.set(result.row.email.toLowerCase().trim(), result.userId);
      }
    }

    const rowsToProcess = displayRows.filter(
      (r) => r.errors.length === 0 && r.permissions.length > 0,
    );

    if (rowsToProcess.length === 0) {
      setPermissionResults([]);
      setUploadStatus("done");
      return;
    }

    setUploadStatus("uploading_permissions");
    setUploadProgress({ completed: 0, total: rowsToProcess.length });

    const results: UploadResult[] = [];

    for (const row of rowsToProcess) {
      const userId = emailToUserId.get(row.email.toLowerCase().trim());
      if (!userId) {
        results.push({
          row,
          error: "User not found — creation may have failed",
        });
        setUploadProgress((prev) => ({
          ...prev,
          completed: prev.completed + 1,
        }));
        continue;
      }
      try {
        await grantPermissions(userId, row.permissions);
        results.push({ row, userId });
      } catch (error) {
        results.push({ row, error: extractErrorMsg(error) });
      }
      setUploadProgress((prev) => ({ ...prev, completed: prev.completed + 1 }));
    }

    setPermissionResults(results);
    setUploadStatus("done");
  };

  const handleClose = () => {
    const didUpload = uploadStatus === "done";
    resetState();
    onClose();
    if (didUpload) onUploadSuccess?.();
  };

  // Derived
  const invalidRows = displayRows.filter((r) => r.errors.length > 0);
  const existingRows = displayRows.filter(
    (r) => r.errors.length === 0 && r.isExisting,
  );
  const newRows = displayRows.filter(
    (r) => r.errors.length === 0 && !r.isExisting,
  );
  const hasRows = displayRows.length > 0;

  // -------------------------------------------------------------------------
  // Column definitions
  // -------------------------------------------------------------------------
  const isEditing = uploadStatus === "idle";

  const editableTextField = (
    field: string,
    headerName: string,
    width: number,
  ): GridColDef => ({ field, headerName, width, editable: isEditing });

  const renderHeaderCheckbox = (key: Permission, label: string) => {
    const validRowsNow = displayRows.filter((r) => r.errors.length === 0);
    const checkedCount = validRowsNow.filter((r) =>
      r.permissions.includes(key),
    ).length;
    const allChecked =
      validRowsNow.length > 0 && checkedCount === validRowsNow.length;
    const indeterminate =
      checkedCount > 0 && checkedCount < validRowsNow.length;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          lineHeight: 1,
          width: "100%",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            maxWidth: "100%",
            textAlign: "center",
            whiteSpace: "normal",
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        <Checkbox
          size="small"
          checked={allChecked}
          indeterminate={indeterminate}
          disabled={validRowsNow.length === 0}
          onChange={() => toggleAllPermission(key)}
          onClick={(e) => e.stopPropagation()}
          sx={{ p: 0 }}
        />
      </Box>
    );
  };

  const permissionColumns: GridColDef[] = assignableAppPermissions(
    getServiceConfig(orgId, isDevEnv).apps ?? [],
  ).flatMap((appPerm) => {
    const isExpanded = expandedApps.has(appPerm.permission);
    const hasSubPerms =
      appPerm.subPermissions && appPerm.subPermissions.length > 0;

    const appLabel = t(appPerm.shortLabelKey ?? appPerm.labelKey);

    const parentCol: GridColDef = {
      field: `perm_${appPerm.permission}`,
      headerName: appLabel,
      width: 90,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        const validRowsNow = displayRows.filter((r) => r.errors.length === 0);
        const checkedCount = validRowsNow.filter((r) =>
          r.permissions.includes(appPerm.permission),
        ).length;
        const allChecked =
          validRowsNow.length > 0 && checkedCount === validRowsNow.length;
        const indeterminate =
          checkedCount > 0 && checkedCount < validRowsNow.length;

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              lineHeight: 1,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0,
                width: "100%",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  whiteSpace: "normal",
                  lineHeight: 1.2,
                  textAlign: "center",
                }}
              >
                {appLabel}
              </Typography>
              {hasSubPerms && (
                <Tooltip
                  title={
                    isExpanded
                      ? "Collapse sub-permissions"
                      : "Expand sub-permissions"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAppExpanded(appPerm.permission);
                    }}
                    sx={{ p: 0, ml: 0.25 }}
                  >
                    {isExpanded ? (
                      <KeyboardArrowDown fontSize="inherit" />
                    ) : (
                      <KeyboardArrowRight fontSize="inherit" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Checkbox
              size="small"
              checked={allChecked}
              indeterminate={indeterminate}
              disabled={validRowsNow.length === 0}
              onChange={() => toggleAllPermission(appPerm.permission)}
              onClick={(e) => e.stopPropagation()}
              sx={{ p: 0 }}
            />
          </Box>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as DisplayRow;
        return (
          <Checkbox
            size="small"
            checked={row.permissions.includes(appPerm.permission)}
            disabled={row.errors.length > 0}
            onChange={() => togglePermission(row.id, appPerm.permission)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    };

    if (!isExpanded || !appPerm.subPermissions) return [parentCol];

    const subCols: GridColDef[] = appPerm.subPermissions.map((sub) => ({
      field: `perm_${sub.permission}`,
      headerName: t(sub.shortLabelKey ?? sub.labelKey),
      width: 80,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () =>
        renderHeaderCheckbox(
          sub.permission,
          t(sub.shortLabelKey ?? sub.labelKey),
        ),
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as DisplayRow;
        const isDisabled =
          row.errors.length > 0 ||
          !row.permissions.includes(appPerm.permission);
        return (
          <Checkbox
            size="small"
            checked={row.permissions.includes(sub.permission)}
            disabled={isDisabled}
            onChange={() => togglePermission(row.id, sub.permission)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    }));

    return [parentCol, ...subCols];
  });

  const deleteRow = (id: number) => {
    setDisplayRows((prev) => {
      const filtered = prev
        .filter((r) => r.id !== id)
        .map((r, i) => ({ ...r, index: i + 1 }));
      return recheckDuplicates(filtered, existingEmails, requireInternalUserId);
    });
  };

  const columns: GridColDef[] = [
    { field: "index", headerName: "#", width: 50, sortable: false },
    {
      field: "_status",
      headerName: "",
      width: 40,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as DisplayRow;
        if (row.errors.length > 0) {
          return (
            <Tooltip title={row.errors.join(" · ")} arrow>
              <ErrorIcon color="error" fontSize="small" />
            </Tooltip>
          );
        }
        if (row.isExisting) {
          return (
            <Tooltip
              title="Existing user — will not be re-created; selected permissions will be applied in step 2"
              arrow
            >
              <Person color="info" fontSize="small" />
            </Tooltip>
          );
        }
        return <CheckCircle color="success" fontSize="small" />;
      },
    },
    editableTextField("first_name", "First Name", 120),
    editableTextField("last_name", "Last Name", 120),
    editableTextField("email", "Email", 220),
    editableTextField("badge_number", "Badge #", 100),
    editableTextField("rank_short", "Rank", 90),
    editableTextField("contact_phone_number", "Telephone", 150),
    ...(requireInternalUserId
      ? [editableTextField("internal_user_id", "Internal User ID", 140)]
      : []),
    ...(uploadStatus !== "idle" ? permissionColumns : []),
    {
      field: "_delete",
      headerName: "",
      width: 40,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={() => deleteRow((params.row as DisplayRow).id)}
        >
          <Delete fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const spinnerLabel =
    uploadStatus === "uploading_users"
      ? `Creating users ${uploadProgress.completed} / ${uploadProgress.total}`
      : `Applying permissions ${uploadProgress.completed} / ${uploadProgress.total}`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>Upload Users CSV</DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {!hasRows ? (
            <FileSelectPhase
              onSelectFile={() => fileInputRef.current?.click()}
              requireInternalUserId={requireInternalUserId}
            />
          ) : uploadStatus === "uploading_users" ||
            uploadStatus === "uploading_permissions" ? (
            <UploadSpinner
              label={spinnerLabel}
              completed={uploadProgress.completed}
              total={uploadProgress.total}
            />
          ) : uploadStatus === "done" ? (
            <ResultsPhase
              userCreationResults={userCreationResults}
              permissionResults={permissionResults}
              existingRows={existingRows}
            />
          ) : (
            <PreviewPhase
              displayRows={displayRows}
              columns={columns}
              uploadStatus={uploadStatus}
              userCreationResults={userCreationResults}
              existingRows={existingRows}
              newRows={newRows}
              invalidRows={invalidRows}
              duplicatesRemoved={duplicatesRemoved}
              fileName={fileName}
              onResetFile={resetState}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={(error) =>
                console.error("Row update error:", error)
              }
            />
          )}

          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </Box>
      </DialogContent>

      {hasRows && (
        <DialogActions>
          <Button onClick={handleClose}>
            {uploadStatus === "done" ? "Close" : "Cancel"}
          </Button>
          {uploadStatus === "idle" && (
            <Button
              variant="contained"
              disabled={
                !orgSettingsLoaded ||
                (newRows.length === 0 && existingRows.length === 0)
              }
              onClick={handleUploadUsers}
            >
              {newRows.length > 0
                ? `Create ${newRows.length} New User${newRows.length !== 1 ? "s" : ""} \u2192`
                : "Continue to Permissions \u2192"}
            </Button>
          )}
          {uploadStatus === "users_done" && (
            <Button variant="contained" onClick={handleGrantPermissions}>
              Apply Permissions
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UploadUsersCSVDialog;
